// src/components/dashboard/cases/newCase/case_id/caseTable/utils/geometry.js
import * as THREE from "three";

export const isPlyUrl = (url) => {
  if (!url) return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  return clean.endsWith(".ply");
};

export const getFileLabel = (item, index) => {
  if (item && typeof item === "object") {
    const explicit = item.name || item.fileName || item.filename || item.label;
    if (explicit) return String(explicit);
  }
  const url = item && typeof item === "object" ? item.url : item;
  if (typeof url === "string" && url) {
    try {
      const clean = url.split("?")[0].split("#")[0];
      const last = decodeURIComponent(clean.split("/").pop() || "");
      if (last) return last;
    } catch {
      /* ignore */
    }
  }
  return "Model " + (index + 1);
};

export const pointInPoly = (pt, loop) => {
  let inside = false;
  const [px, py] = pt;
  for (let i = 0, j = loop.length - 1; i < loop.length; j = i++) {
    const [xi, yi] = loop[i];
    const [xj, yj] = loop[j];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

export const computeLoopsArea = (segs) => {
  const key = (x, y) => x + "," + y;
  const map = new Map();
  const add = (k, i) => {
    const l = map.get(k);
    if (l) l.push(i);
    else map.set(k, [i]);
  };
  segs.forEach((s, i) => {
    add(key(s.x1, s.y1), i);
    add(key(s.x2, s.y2), i);
  });

  const used = new Uint8Array(segs.length);
  const loops = [];
  for (let i = 0; i < segs.length; i++) {
    if (used[i]) continue;
    used[i] = 1;
    const s0 = segs[i];
    const loop = [[s0.x1, s0.y1], [s0.x2, s0.y2]];
    let endKey = key(s0.x2, s0.y2);
    for (;;) {
      const list = map.get(endKey);
      if (!list) break;
      let next = -1;
      for (let k = 0; k < list.length; k++) {
        if (!used[list[k]]) { next = list[k]; break; }
      }
      if (next < 0) break;
      used[next] = 1;
      const s = segs[next];
      if (key(s.x1, s.y1) === endKey) {
        loop.push([s.x2, s.y2]);
        endKey = key(s.x2, s.y2);
      } else {
        loop.push([s.x1, s.y1]);
        endKey = key(s.x1, s.y1);
      }
    }
    loops.push(loop);
  }

  const signedArea = (L) => {
    let a = 0;
    for (let k = 0; k < L.length; k++) {
      const [xa, ya] = L[k];
      const [xb, yb] = L[(k + 1) % L.length];
      a += xa * yb - xb * ya;
    }
    return a / 2;
  };

  const info = loops
    .filter((L) => L.length >= 3)
    .map((L) => ({ L, a: Math.abs(signedArea(L)) }));

  if (info.length > 400) return info.reduce((s, o) => s + o.a, 0);

  let total = 0;
  for (const o of info) {
    let depth = 0;
    for (const p of info) {
      if (p !== o && p.a > o.a && pointInPoly(o.L[0], p.L)) depth++;
    }
    total += depth % 2 === 0 ? o.a : -o.a;
  }
  return Math.max(total, 0);
};

export const closestParamOnLine = (ray, P, D) => {
  const u = ray.direction;
  const w = new THREE.Vector3().subVectors(ray.origin, P);
  const b = u.dot(D);
  const d = u.dot(w);
  const e = D.dot(w);
  const denom = 1 - b * b;
  if (denom < 1e-4) return null;
  return (e - b * d) / denom;
};